/**
 * Backfill Orchestration service for SubContractorHub (Story SCH-3.2).
 *
 * Wraps four endpoints:
 *   - POST /start-backfill       (admin / superadmin)
 *   - GET  /sync-progress/{id}   (any authenticated tenant user)
 *   - POST /continue-backfill/{id} (admin / superadmin)
 *   - GET  /active-backfill      (any authenticated tenant user)
 *
 * Mirrors backfillPreviewService conventions: shared fetchJson helper,
 * snake↔camel translation at the boundary, discriminated union result.
 *
 * Forbidden (HTTP 403) is treated as a normal error result (not a thrown
 * exception) so callers can render the "your role isn't allowed" UI without
 * try/catch noise.
 */

import { supabase } from '@/lib/supabaseClient';
import type {
  ActiveBackfillResponse,
  BackfillMutationError,
  BackfillMutationResult,
  ContinueBackfillResponse,
  StartBackfillResponse,
  SyncHistoryItem,
  SyncHistoryResponse,
  SyncHistoryStatus,
  SyncHistoryType,
  SyncProgressFailure,
  SyncProgressResponse,
  SyncProgressStatus,
} from '@/types/integrations/backfillProgress';

const BACKEND_URL =
  import.meta.env.VITE_PYTHON_BACKEND_URL || 'http://localhost:8000';

async function authHeader(): Promise<HeadersInit> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('Not authenticated');
  }
  return {
    Authorization: `Bearer ${session.access_token}`,
    'Content-Type': 'application/json',
  };
}

interface ApiEnvelope<TData> {
  data: TData | null;
  error: { code: string; message: string; details?: Record<string, unknown> } | null;
  meta: unknown;
}

async function fetchEnvelope<TData>(
  path: string,
  init: RequestInit,
): Promise<ApiEnvelope<TData> | { _http_403: true }> {
  const headers = await authHeader();
  const response = await fetch(`${BACKEND_URL}${path}`, {
    ...init,
    headers: { ...headers, ...(init.headers ?? {}) },
  });

  // Admin-gated endpoints raise HTTP 403 (FastAPI dependency-injection)
  // rather than envelope errors. Surface as a typed result.
  if (response.status === 403) {
    return { _http_403: true };
  }

  if (!response.ok && response.status !== 200) {
    let detail: string | undefined;
    try {
      const json = await response.json();
      detail = typeof json?.detail === 'string' ? json.detail : undefined;
    } catch {
      // ignore parse error
    }
    throw new Error(detail || `Request failed with status ${response.status}`);
  }
  return (await response.json()) as ApiEnvelope<TData>;
}

const KNOWN_ERROR_CODES = [
  'auth_failed',
  'integration_disabled',
  'integration_not_found',
  'backfill_already_running',
  'backfill_already_complete',
  'backfill_already_paused',
  'sync_run_not_found',
  'forbidden',
  'internal_error',
] as const;

function _normalizeError(
  raw: { code: string; message: string; details?: Record<string, unknown> } | null,
): BackfillMutationError {
  if (!raw) {
    return { code: 'internal_error', message: 'Unknown error' };
  }
  const code = (KNOWN_ERROR_CODES as readonly string[]).includes(raw.code)
    ? (raw.code as BackfillMutationError['code'])
    : 'internal_error';
  return { code, message: raw.message, details: raw.details };
}

// ─── Snake → camel mappers ────────────────────────────────────────────────

interface RawStartBackfillResponse {
  sync_run_id: string;
  status: 'started' | 'already_running';
  batch_size: number;
}

function _mapStart(raw: RawStartBackfillResponse): StartBackfillResponse {
  return {
    syncRunId: raw.sync_run_id,
    status: raw.status,
    batchSize: raw.batch_size,
  };
}

interface RawSyncProgressFailure {
  subhub_id: string;
  subhub_name: string;
  reason: string;
  email_redacted: string | null;
  detail: string | null;
  occurred_at: string;
}

function _mapFailure(raw: RawSyncProgressFailure): SyncProgressFailure {
  return {
    subhubId: raw.subhub_id,
    subhubName: raw.subhub_name,
    reason: raw.reason,
    emailRedacted: raw.email_redacted ?? null,
    detail: raw.detail ?? null,
    occurredAt: raw.occurred_at,
  };
}

interface RawSyncProgressResponse {
  sync_run_id: string;
  status: SyncProgressStatus;
  total_catalog: number | null;
  total_processed: number;
  total_success: number;
  total_skipped_unchanged: number;
  total_failed: number;
  fallback_owner_used_count: number;
  current_batch_n: number;
  batch_size: number;
  last_subhub_id: string | null;
  recent_failures: RawSyncProgressFailure[];
  started_at: string;
  finished_at: string | null;
  awaiting_continue: boolean;
  paused_reason: string | null;
  auto_continue?: boolean;  // SCH-3.9; optional for backward-compat with older backend deploys.
}

function _mapProgress(raw: RawSyncProgressResponse): SyncProgressResponse {
  return {
    syncRunId: raw.sync_run_id,
    status: raw.status,
    totalCatalog: raw.total_catalog,
    totalProcessed: raw.total_processed,
    totalSuccess: raw.total_success,
    totalSkippedUnchanged: raw.total_skipped_unchanged,
    totalFailed: raw.total_failed,
    fallbackOwnerUsedCount: raw.fallback_owner_used_count,
    currentBatchN: raw.current_batch_n,
    batchSize: raw.batch_size,
    lastSubhubId: raw.last_subhub_id,
    recentFailures: (raw.recent_failures ?? []).map(_mapFailure),
    startedAt: raw.started_at,
    finishedAt: raw.finished_at ?? null,
    awaitingContinue: raw.awaiting_continue,
    pausedReason: raw.paused_reason ?? null,
    autoContinue: raw.auto_continue ?? false,
  };
}

interface RawContinueBackfillResponse {
  sync_run_id: string;
  status: 'continuing' | 'already_complete' | 'already_running';
}

function _mapContinue(raw: RawContinueBackfillResponse): ContinueBackfillResponse {
  return {
    syncRunId: raw.sync_run_id,
    status: raw.status,
  };
}

interface RawActiveBackfillResponse {
  sync_run_id: string;
  status: SyncProgressStatus;
  total_processed: number;
  total_catalog: number | null;
  started_at: string;
  last_activity_at: string | null;
}

function _mapActive(raw: RawActiveBackfillResponse): ActiveBackfillResponse {
  return {
    syncRunId: raw.sync_run_id,
    status: raw.status,
    totalProcessed: raw.total_processed,
    totalCatalog: raw.total_catalog,
    startedAt: raw.started_at,
    lastActivityAt: raw.last_activity_at ?? null,
  };
}

// ─── Public API ───────────────────────────────────────────────────────────

async function startBackfill(
  options: {
    batchSize?: number;
    /**
     * POC import mode: when supplied, the backend processes only SCH
     * projects whose subhub_uuid is in this list. Used by the FE
     * "Import these N (POC test mode)" button.
     */
    subhubUuidFilter?: string[];
    /**
     * SCH-3.9 auto-continue mode: when true, the backend chains batches
     * automatically until catalog_exhausted, paused (error), or the
     * integration is disabled — no Continue clicks required between
     * batches. Default false preserves the SCH-3.2 gated flow.
     */
    autoContinue?: boolean;
  } = {},
): Promise<BackfillMutationResult<StartBackfillResponse>> {
  const body: Record<string, unknown> = {
    batch_size: options.batchSize ?? 50,
  };
  if (options.subhubUuidFilter && options.subhubUuidFilter.length > 0) {
    body.subhub_uuid_filter = options.subhubUuidFilter;
  }
  if (options.autoContinue) {
    body.auto_continue = true;
  }

  const result = await fetchEnvelope<RawStartBackfillResponse>(
    '/api/v1/integrations/subcontractorhub/start-backfill',
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
  );

  if ('_http_403' in result) {
    return {
      data: null,
      error: {
        code: 'forbidden',
        message:
          'Administrator access is required to start a backfill.',
      },
    };
  }
  if (result.error) {
    return { data: null, error: _normalizeError(result.error) };
  }
  if (!result.data) {
    return {
      data: null,
      error: { code: 'internal_error', message: 'Empty response from backend' },
    };
  }
  return { data: _mapStart(result.data), error: null };
}

async function getSyncProgress(
  syncRunId: string,
): Promise<BackfillMutationResult<SyncProgressResponse>> {
  const result = await fetchEnvelope<RawSyncProgressResponse>(
    `/api/v1/integrations/subcontractorhub/sync-progress/${encodeURIComponent(syncRunId)}`,
    { method: 'GET' },
  );

  if ('_http_403' in result) {
    return {
      data: null,
      error: { code: 'forbidden', message: 'Forbidden' },
    };
  }
  if (result.error) {
    return { data: null, error: _normalizeError(result.error) };
  }
  if (!result.data) {
    return {
      data: null,
      error: { code: 'sync_run_not_found', message: 'No backfill run found' },
    };
  }
  return { data: _mapProgress(result.data), error: null };
}

async function continueBackfill(
  syncRunId: string,
  options: {
    /**
     * SCH-3.9 mid-run upgrade: when true, this Continue click flips the
     * run into auto-continue mode for the remainder. Backend chains the
     * rest of the batches automatically + updates the run_started marker
     * so subsequent polls see autoContinue=true.
     */
    autoContinue?: boolean;
  } = {},
): Promise<BackfillMutationResult<ContinueBackfillResponse>> {
  const body: Record<string, unknown> = {};
  if (options.autoContinue) {
    body.auto_continue = true;
  }
  const result = await fetchEnvelope<RawContinueBackfillResponse>(
    `/api/v1/integrations/subcontractorhub/continue-backfill/${encodeURIComponent(syncRunId)}`,
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
  );

  if ('_http_403' in result) {
    return {
      data: null,
      error: {
        code: 'forbidden',
        message:
          'Administrator access is required to release the next batch.',
      },
    };
  }
  if (result.error) {
    return { data: null, error: _normalizeError(result.error) };
  }
  if (!result.data) {
    return {
      data: null,
      error: { code: 'internal_error', message: 'Empty response from backend' },
    };
  }
  return { data: _mapContinue(result.data), error: null };
}

async function abortBackfill(
  syncRunId: string,
): Promise<BackfillMutationResult<{ sync_run_id: string; status: string }>> {
  const result = await fetchEnvelope<{ sync_run_id: string; status: string }>(
    `/api/v1/integrations/subcontractorhub/abort-run/${encodeURIComponent(syncRunId)}`,
    { method: 'POST', body: JSON.stringify({}) },
  );

  if ('_http_403' in result) {
    return {
      data: null,
      error: {
        code: 'forbidden',
        message:
          'Administrator access is required to abort a backfill.',
      },
    };
  }
  if (result.error) {
    return { data: null, error: _normalizeError(result.error) };
  }
  if (!result.data) {
    return {
      data: null,
      error: { code: 'internal_error', message: 'Empty response from backend' },
    };
  }
  return { data: result.data, error: null };
}

// ─── SCH-4.2: POST /sync-now (tenant-wide immediate sync) ─────────────────
//
// Distinct from start-backfill: no body, always full-catalog, always
// auto_continue=True, started_by='manual:sync_now'. Returns just
// {sync_run_id, status: 'started'} on success; same error envelopes as
// start-backfill for overlap/auth/disabled/not-found.

interface RawSyncNowResponse {
  sync_run_id: string;
  status: 'started';
}

async function syncNow(): Promise<
  BackfillMutationResult<{ syncRunId: string; status: 'started' }>
> {
  const result = await fetchEnvelope<RawSyncNowResponse>(
    '/api/v1/integrations/subcontractorhub/sync-now',
    { method: 'POST' },
  );

  if ('_http_403' in result) {
    return {
      data: null,
      error: {
        code: 'forbidden',
        message:
          'Administrator access is required to trigger a manual sync.',
      },
    };
  }
  if (result.error) {
    return { data: null, error: _normalizeError(result.error) };
  }
  if (!result.data) {
    return {
      data: null,
      error: { code: 'internal_error', message: 'Empty response from backend' },
    };
  }
  return {
    data: { syncRunId: result.data.sync_run_id, status: result.data.status },
    error: null,
  };
}

async function getActiveBackfill(): Promise<
  BackfillMutationResult<ActiveBackfillResponse | null>
> {
  const result = await fetchEnvelope<RawActiveBackfillResponse | null>(
    '/api/v1/integrations/subcontractorhub/active-backfill',
    { method: 'GET' },
  );

  if ('_http_403' in result) {
    return {
      data: null,
      error: { code: 'forbidden', message: 'Forbidden' },
    };
  }
  if (result.error) {
    return { data: null, error: _normalizeError(result.error) };
  }
  // data === null is a valid "no active run" signal.
  if (result.data === null) {
    return { data: null, error: null };
  }
  return { data: _mapActive(result.data), error: null };
}

interface RawSyncHistoryItem {
  sync_run_id: string;
  started_at: string;
  finished_at: string | null;
  duration_seconds: number | null;
  type: SyncHistoryType;
  status: SyncHistoryStatus;
  paused_reason: string | null;
  projects_succeeded: number;
  projects_failed: number;
  projects_skipped: number;
  errors_count: number;
  started_by: string | null;
}

interface RawSyncHistoryResponse {
  data: RawSyncHistoryItem[];
  total_count: number;
  limit: number;
  offset: number;
}

function _mapHistoryItem(raw: RawSyncHistoryItem): SyncHistoryItem {
  return {
    syncRunId: raw.sync_run_id,
    startedAt: raw.started_at,
    finishedAt: raw.finished_at ?? null,
    durationSeconds: raw.duration_seconds ?? null,
    type: raw.type,
    status: raw.status,
    pausedReason: raw.paused_reason ?? null,
    projectsSucceeded: raw.projects_succeeded ?? 0,
    projectsFailed: raw.projects_failed ?? 0,
    projectsSkipped: raw.projects_skipped ?? 0,
    errorsCount: raw.errors_count ?? 0,
    startedBy: raw.started_by ?? null,
  };
}

async function getSyncHistory(options: {
  limit?: number;
  offset?: number;
  status?: SyncHistoryStatus;
  type?: SyncHistoryType;
} = {}): Promise<BackfillMutationResult<SyncHistoryResponse>> {
  const params = new URLSearchParams();
  if (options.limit !== undefined) params.set('limit', String(options.limit));
  if (options.offset !== undefined) params.set('offset', String(options.offset));
  if (options.status) params.set('status', options.status);
  if (options.type) params.set('type', options.type);
  const qs = params.toString();
  const path = `/api/v1/integrations/subcontractorhub/sync-history${qs ? `?${qs}` : ''}`;

  const result = await fetchEnvelope<RawSyncHistoryResponse>(path, { method: 'GET' });
  if ('_http_403' in result) {
    return {
      data: null,
      error: {
        code: 'forbidden',
        message:
          'Administrator access is required to view sync history.',
      },
    };
  }
  if (result.error) {
    return { data: null, error: _normalizeError(result.error) };
  }
  if (!result.data) {
    return {
      data: null,
      error: { code: 'internal_error', message: 'Empty response from backend' },
    };
  }
  return {
    data: {
      data: (result.data.data ?? []).map(_mapHistoryItem),
      totalCount: result.data.total_count,
      limit: result.data.limit,
      offset: result.data.offset,
    },
    error: null,
  };
}

export const backfillProgressService = {
  startBackfill,
  getSyncProgress,
  continueBackfill,
  abortBackfill,
  getActiveBackfill,
  getSyncHistory,
  syncNow,
};
